import { useState } from "react";

import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import Select from "@mui/material/Select";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  useCompanyMembers,
  useRoleContext,
  useWriterComponentState,
} from "hooks";
import { MODAL_STYLE } from "layouts/constants";

import { ClientAppEvent } from "../../../../../../constants";

import type { SelectChangeEvent } from "@mui/material/Select";

interface Props {
  shipment: ShipperCondensedShipment;
  onModalClose: VoidFunction;
  onTeammatesAdded: VoidFunction;
  modalOpen: boolean;
}

export function ShareShipmentWithTeammatesModal(props: Props) {
  const { modalOpen, shipment, onModalClose, onTeammatesAdded } = props;
  const { role } = useRoleContext();
  const posthog = usePostHog();
  const {
    members,
    error: CompanyMemberFetchError,
    loading: CompanyMemberFetchLoading,
  } = useCompanyMembers();
  const [newShareesIds, setNewShareesIds] = useState<number[]>([]);
  const { loading, write, error, resetState } = useWriterComponentState({
    errorMessage:
      "Failed to add teammates. Please contact us if the issue persists",
    writeConfig: {
      method: "patch",
      url: shipment?.info?.id ? `/shipments/${shipment.info.id}/update` : "/",
      body: { sharee_role_ids: newShareesIds },
    },
  });

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const onClose = () => {
    resetState();
    setNewShareesIds([]);
    onModalClose();
  };

  const submit = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_SHARED_WITH_TEAMMATES, {
        teammates_count: newShareesIds.length,
      });
      onClose();
      onTeammatesAdded();
    });
  };

  const getCompanyMemberLabel = (member: CompanyMember) =>
    `${member.user.first_name ?? ""} ${member.user.last_name ?? ""}`;

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const {
      target: { value },
    } = event;
    if (typeof value === "string") {
      const roleStringArray = value.split(",");
      setNewShareesIds(
        roleStringArray.map((stringRoleId) => parseInt(stringRoleId, 10)),
      );
    } else {
      setNewShareesIds(value);
    }
  };

  if (!role) {
    return null;
  }

  const ownerRoleId = shipment.info.role;
  const selectableTeammates = members.filter(
    (member) => member.id !== role.id && member.id !== ownerRoleId,
  );
  const roleIdMap: RoleIdToCompanyMemberMap =
    selectableTeammates.reduce<RoleIdToCompanyMemberMap>(
      (acc, member: CompanyMember) => {
        acc[member.id] = member;
        return acc;
      },
      {},
    );

  const existingShareesIdSet = new Set(
    shipment.sharees.map((sharee) => sharee.sharee.id),
  );

  return (
    <Modal open={modalOpen} onClose={onClose}>
      <Stack sx={MODAL_STYLE} direction="column" spacing={2}>
        <Typography variant="h6" textAlign="center">
          Add Teammates
        </Typography>
        <EvotruxDivider />
        <Typography variant="body1" textAlign="center">
          Select the teammates you would like to share this shipment with.
        </Typography>
        <LoadingWrappedNode loading={CompanyMemberFetchLoading}>
          <Stack>
            <FormControl>
              <InputLabel>Teammates</InputLabel>
              <Select
                multiple
                value={newShareesIds}
                onChange={handleChange}
                input={<OutlinedInput label="Chip" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((roleId: number) => (
                      <Chip
                        key={roleId}
                        label={getCompanyMemberLabel(roleIdMap[roleId])}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {selectableTeammates.map((member: CompanyMember) => (
                  <MenuItem
                    key={member.id}
                    value={member.id}
                    disabled={existingShareesIdSet.has(member.id)}
                  >
                    {`${getCompanyMemberLabel(member)} ${existingShareesIdSet.has(member.id) ? "(Already shared)" : ""}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ErrorTypography error={CompanyMemberFetchError} />
          </Stack>
        </LoadingWrappedNode>
        <LoadingWrappedNode loading={loading}>
          <Stack alignItems="center" mt="2rem">
            <Button variant="contained" onClick={submit} size="large">
              Add Teammates
            </Button>
          </Stack>
        </LoadingWrappedNode>
        <ErrorTypography error={error} />
      </Stack>
    </Modal>
  );
}
